import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import type { Scope } from "@/api/types";

const STORAGE_KEY = "fin:scope";
const SCOPES: Scope[] = ["all", "personal", "business"];

export function readStoredScope(): Scope {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value && (SCOPES as string[]).includes(value) ? (value as Scope) : "all";
  } catch {
    return "all";
  }
}

type ScopeContextValue = { scope: Scope; setScope: (scope: Scope) => void };
const ScopeContext = createContext<ScopeContextValue | null>(null);

export function ScopeProvider({ children, initial }: { children: ReactNode; initial?: Scope }) {
  const [scope, setScopeState] = useState<Scope>(() => initial ?? readStoredScope());
  const setScope = useCallback((next: Scope) => {
    setScopeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage unavailable (private mode): keep it in memory only
    }
  }, []);
  const value = useMemo(() => ({ scope, setScope }), [scope, setScope]);
  return <ScopeContext value={value}>{children}</ScopeContext>;
}

export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useScope must be used inside <ScopeProvider>");
  return ctx;
}
